/**
 * Turning the `alipay_page2` record into `AccountProfile` (MYP-73).
 *
 * This adapter is an allow-list and that is the whole point of it. The endpoint returns **99
 * fields** where the document prints four, and among them are the account's SMTP host, user and
 * sending address, its Facebook, Google, Apple, WeChat and UnionID identifiers, its referrer, its
 * sales owner and an `is_deleted` flag. None of that belongs on a profile screen, and a
 * strip-list — "everything except the ones we happen to have noticed" — would put the next field
 * the vendor adds on screen by default. Reading named fields means a new one is invisible until
 * someone deliberately adds it.
 *
 * `code` lives among those fields rather than wrapping them; see the `flatRecord` envelope. The
 * record also has its own `status`, and it is the account's KYC state, not a result code.
 */

import type { AccountProfile, IdentityVerification } from './account-profile.js';

type Row = Readonly<Record<string, unknown>>;

function text(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

/**
 * A decimal amount on the wire (`"2459.90"`) → integer cents.
 *
 * Undefined for anything unreadable or negative rather than a guess: a wallet cannot hold less than
 * nothing, so a negative figure is a record this adapter does not understand, not a debt to show.
 */
function cents(value: unknown): number | undefined {
  const raw = text(value);
  if (raw === undefined) return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed * 100) : undefined;
}

/**
 * The name on the account — `firstname` and `lastname`, in that order, joined by a space.
 *
 * Neither is altered: a record whose two fields are identical reads as both, because collapsing
 * "Lee Lee" to "Lee" would change a real person's name to fix an ugly test account's. Undefined
 * when both are blank.
 */
function fullName(record: Row): string | undefined {
  const parts = [text(record['firstname']), text(record['lastname'])].filter(
    (part): part is string => part !== undefined,
  );
  return parts.length === 0 ? undefined : parts.join(' ');
}

/**
 * `auto_inbound` is `"Y"` / `"N"` on the wire.
 *
 * Anything else reads as off: this drives whether the warehouse books a parcel in without asking,
 * and defaulting an unrecognised value to "on" would claim a standing permission the customer may
 * never have given.
 */
function isYes(value: unknown): boolean {
  return text(value)?.toUpperCase() === 'Y';
}

/**
 * `payment` is MANUAL or AUTO. Anything else is manual — the mode that asks before taking money.
 */
function paymentMode(value: unknown): AccountProfile['paymentMode'] {
  return text(value)?.toUpperCase() === 'AUTO' ? 'AUTO' : 'MANUAL';
}

/**
 * The account's VIP tier, as "n / max" — `uservip` against `maxvip`.
 *
 * Rendered as a bare pair rather than a name because the vendor publishes no tier names anywhere:
 * `uservip` is `"0"` and `maxvip` is `"5"`, and the fixture's invented `"VIP1"` had no source.
 * Showing 0 of 5 says exactly what is known. Inventing "Bronze" would not.
 */
function vipTier(record: Row): string {
  const tier = text(record['uservip']) ?? '0';
  const max = text(record['maxvip']);
  return max === undefined ? `VIP${tier}` : `VIP${tier} / ${max}`;
}

/**
 * The `status` values that mean the customer's identity is verified (MYP-142).
 *
 * Only `AUTH` is known: seen live on MP66184 and on MP275466 once it was verified (19 Oct 2026).
 * The vendor has not yet listed the other values (api-issues #4). Add one here only once they
 * confirm it means verified.
 */
export const VERIFIED_ACCOUNT_STATUSES: ReadonlySet<string> = new Set(['AUTH']);

/**
 * `alipay_page2`'s `status` → whether the customer may create orders (MYP-142).
 *
 * Fail safe: anything that is not a known "verified" value — blank, missing, or a value the vendor
 * has not explained — reads as not verified. A wrongly blocked customer sees a screen that says
 * why and how to fix it; a wrongly allowed one fills in the whole order and is refused at the end.
 */
export function identityVerification(value: unknown): IdentityVerification {
  const code = text(value)?.toUpperCase();
  return code !== undefined && VERIFIED_ACCOUNT_STATUSES.has(code) ? 'verified' : 'notVerified';
}

/**
 * The record → the profile the Account screen is allowed to show, or undefined if it is not a
 * record at all.
 *
 * `status` is deliberately not read from the vendor's `status` field. That field holds a KYC state
 * (`"AUTH"`), and `AccountProfile.status` is an `'ACTIVE' | 'INACTIVE'` pill that MYP-72 carried
 * over from the mentor mockup and flagged as unconfirmed. The live record settles it: no field
 * means that. `activestatus` (`"NORMAL"`) is the closest thing, so it decides the pill, and
 * anything other than NORMAL reads as inactive rather than being mapped to a guess.
 *
 * The vendor's `status` is read for one thing only: `verification` (MYP-142). Nothing else about
 * the identity check — the ID number aside, which was already read — and never the ID image paths
 * the record also carries.
 */
export function normaliseAccountProfile(record: unknown): AccountProfile | undefined {
  if (typeof record !== 'object' || record === null) return undefined;
  const row = record as Row;

  const customerId = text(row['userno']);
  if (customerId === undefined) return undefined;

  return {
    customerId,
    vipTier: vipTier(row),
    status: text(row['activestatus'])?.toUpperCase() === 'NORMAL' ? 'ACTIVE' : 'INACTIVE',
    verification: identityVerification(row['status']),
    // As the vendor spells it — "RM", not "MYR". Normalised wherever it is formatted.
    walletCurrency: text(row['currency']) ?? 'RM',
    // The account's own balance. `freeze` (the held amount) is deliberately not read: nothing in
    // the document says what it counts, and MYP-54 already flags on-hold as having no source.
    walletBalanceCents: cents(row['balance']),
    paymentMode: paymentMode(row['payment']),
    claimType: text(row['claimpkgtype']) ?? 'MANUAL',
    autoInbound: isYes(row['auto_inbound']),
    /*
      The customer's chosen default, which is blank on this account. `wh_attention` sits next to it
      holding "4,5,28,19,3,3" — a list of warehouses the customer follows, with a duplicate in it —
      and is emphatically not the same question. Left undefined, which is what the Dashboard's
      ship-to card already falls back from.
    */
    defaultWarehouseId: text(row['warehouseid']),
    /*
      `accountlink` is the nearest thing to a linked Taobao account in the record, and it is empty
      here, so the mapping is unconfirmed. Read anyway: an empty value renders as no linked account,
      which is both what this account has and what a wrong mapping would look like — the failure
      mode is invisible either way, unlike inventing a value.
    */
    taobaoAccount: text(row['accountlink']),

    /*
      Identity, read from the record. With no login endpoint (MYP-75) the vendor resolves every
      call to one fixed account (MP66184, the customer number the mentor's own mockup uses), so
      these belong to that account and not to whoever is looking at the screen. They are read
      anyway, by decision, until MYP-75 scopes the API to the signed-in customer; the NRIC is only
      ever rendered through `maskNric`.

      Phone is `telephone` first, then `phone`: the live record fills the former and leaves the
      latter blank.
    */
    name: fullName(row),
    phone: text(row['telephone']) ?? text(row['phone']),
    email: text(row['email']),
    nric: text(row['idno']),
  };
}
